import { motion } from 'framer-motion';
import { AlertCircle, UserPlus, Brain, Zap, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

function generateCode(name: string): string {
  const initials = name.replace(/\s+/g, '').toUpperCase().slice(0, 4).padEnd(4, 'X');
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `SI-${initials}-${rand}`;
}

const BENEFITS = [
  { icon: Brain, text: 'AI-assisted session analysis' },
  { icon: Zap, text: 'Pattern detection across sessions' },
  { icon: ShieldCheck, text: 'Responsible, coach-led insights' },
];

export function SIRegisterPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreedTerms: false,
    agreedPrivacy: false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!form.agreedTerms || !form.agreedPrivacy) {
      setError('Please accept the terms and privacy policy to continue.');
      return;
    }

    setIsLoading(true);
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: { data: { full_name: form.fullName } },
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Registration failed. Please try again.');

      const uid = authData.user.id;

      const { error: userError } = await supabase.from('users').insert({
        id: uid,
        email: form.email,
        full_name: form.fullName,
        role: 'coach',
      });
      if (userError && !userError.message.includes('duplicate')) throw userError;

      const assessmentCode = generateCode(form.fullName);
      const { error: coachError } = await supabase.from('coaches').insert({
        user_id: uid,
        tier: 'basic',
        assessment_code: assessmentCode,
        ai_analysis_access: false,
        manual_ai_access: false,
        is_active: true,
        teams_access_enabled: false,
        crm_enabled: false,
        app_source: 'si',
        si_unlocked: false,
      });
      if (coachError) throw coachError;

      await supabase.from('user_agreements').insert({
        user_id: uid,
        privacy_accepted: true,
        privacy_accepted_at: new Date().toISOString(),
        terms_accepted: true,
        terms_accepted_at: new Date().toISOString(),
        disclaimer_accepted: true,
        disclaimer_accepted_at: new Date().toISOString(),
        gdpr_accepted: true,
        gdpr_accepted_at: new Date().toISOString(),
      });

      // Send welcome email with login details and live walkthrough info
      await supabase.functions.invoke('send-email', {
        body: {
          type: 'welcome-si-register',
          recipientEmail: form.email,
          recipientName: form.fullName,
          password: form.password,
          loginUrl: 'https://sessionintelligence.app/si/login',
        },
      });

      navigate('/si/app/dashboard');
    } catch (err: any) {
      if (err.message?.includes('User already registered') || err.message?.includes('already been registered')) {
        setError('An account with this email already exists. Please sign in instead.');
      } else {
        setError(err.message || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-80 flex flex-col lg:flex-row">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-2/5 flex-col justify-between p-12 bg-gray-80">
        <div>
          <div className="flex items-center gap-2.5 mb-12">
            <div className="p-2 bg-success-40 rounded-xl">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-semibold text-sm">Session Intelligence™</span>
          </div>
          <h2 className="text-3xl font-bold text-white mb-4 leading-snug">
            Your coaching intelligence centre starts here
          </h2>
          <p className="text-gray-40 text-sm leading-relaxed mb-10">
            Create your free account and access a full preview of what Session Intelligence™ can do — before your first report is even uploaded.
          </p>
          <div className="space-y-4">
            {BENEFITS.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="p-1.5 bg-success-40/20 rounded-lg flex-shrink-0">
                  <Icon className="w-4 h-4 text-success-40" />
                </div>
                <span className="text-sm text-gray-30">{text}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-gray-50 leading-relaxed">
          Session Intelligence™ provides coaching hypotheses only. It does not replace the professional judgement of the coach.
        </p>
      </div>

      {/* Right panel */}
      <div className="flex-1 bg-brand-5 flex flex-col">
        <div className="py-5 px-6 flex items-center justify-between lg:justify-end">
          <Link to="/" className="flex items-center gap-2 lg:hidden">
            <div className="p-1.5 bg-success-40 rounded-lg">
              <Brain className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-semibold text-gray-80">Session Intelligence™</span>
          </Link>
          <Link to="/si/login" className="text-sm font-medium text-gray-50 hover:text-gray-80 transition-colors">
            Already have an account? <span className="text-success-50">Sign in</span>
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-8">
          <div className="w-full max-w-md">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-success-10 text-success-50 text-xs font-semibold mb-4">
                Free Early Access
              </span>
              <h1 className="text-2xl font-bold text-gray-80 mb-1.5">Create your free account</h1>
              <p className="text-sm text-gray-50">Access the full dashboard preview — no credit card required</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              className="bg-white rounded-3xl border border-gray-20 shadow-sm p-8"
            >
              {error && (
                <div className="flex items-center gap-2 bg-red-50 text-red-700 p-4 rounded-2xl mb-6 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-80 mb-1.5">Full name</label>
                  <input
                    type="text" name="fullName" value={form.fullName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-2xl border border-gray-20 bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors text-sm"
                    placeholder="Jane Smith" required disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-80 mb-1.5">Email address</label>
                  <input
                    type="email" name="email" value={form.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-2xl border border-gray-20 bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors text-sm"
                    placeholder="you@example.com" required disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-80 mb-1.5">Password</label>
                  <input
                    type="password" name="password" value={form.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-2xl border border-gray-20 bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors text-sm"
                    placeholder="Min. 8 characters" required disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-80 mb-1.5">Confirm password</label>
                  <input
                    type="password" name="confirmPassword" value={form.confirmPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-2xl border border-gray-20 bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors text-sm"
                    placeholder="Re-enter your password" required disabled={isLoading}
                  />
                </div>

                <div className="pt-2 space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox" name="agreedPrivacy" checked={form.agreedPrivacy}
                      onChange={handleChange} disabled={isLoading}
                      className="mt-0.5 w-4 h-4 rounded border-gray-30 accent-success-50"
                    />
                    <span className="text-xs text-gray-50 leading-relaxed">
                      I have read and agree to the{' '}
                      <Link to="/privacy" target="_blank" className="text-success-50 hover:underline">Privacy Policy</Link>
                      {' '}and understand how my data is used.
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox" name="agreedTerms" checked={form.agreedTerms}
                      onChange={handleChange} disabled={isLoading}
                      className="mt-0.5 w-4 h-4 rounded border-gray-30 accent-success-50"
                    />
                    <span className="text-xs text-gray-50 leading-relaxed">
                      I agree to the{' '}
                      <Link to="/terms" target="_blank" className="text-success-50 hover:underline">Terms of Service</Link>
                      {' '}and{' '}
                      <Link to="/disclaimer" target="_blank" className="text-success-50 hover:underline">Disclaimer</Link>.
                      I understand Session Intelligence™ provides coaching hypotheses, not clinical assessments.
                    </span>
                  </label>
                </div>

                <button
                  type="submit" disabled={isLoading}
                  className="w-full btn-primary justify-center mt-2 inline-flex items-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  {isLoading ? 'Creating account…' : 'Create Free Account'}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
